import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { AddIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
	Badge,
	Button,
	ButtonGroup,
	Card,
	CardBody,
	CardHeader,
	Flex,
	FormControl,
	HStack,
	Icon,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Select,
	SimpleGrid,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue,
	useDisclosure,
	useToast,
	VStack,
} from "@chakra-ui/react";
import { FiCheckSquare, FiChevronLeft, FiChevronRight } from "react-icons/fi";

import AssigneeSelect from "../../Components/Tasks/AssigneeSelect";
import TaskDetailModal, { DueDateBadge, dueDateUrgency, statusColor } from "../../Components/Tasks/TaskDetailModal";
import TaskFormModal from "../../Components/Tasks/TaskFormModal";
import { PropertyConfirmModal } from "../../Components/PropertyModal/shared";
import {
	deleteAgencyTaskAsync,
	listAgencyTaskAssigneesAsync,
	listAgencyTasksAsync,
	setTaskFilters,
} from "../../redux/tasks/tasksSlice";
import { normalizeAssigneeKey, suggestionOptions } from "../../utils/taskAssignees";

export default function Tasks() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const toast = useToast();
	const textColor = useColorModeValue("gray.700", "white");
	const tasks = useSelector((state) => state.tasks.items);
	const nextCursor = useSelector((state) => state.tasks.nextCursor);
	const listStatus = useSelector((state) => state.tasks.listStatus);
	const assignees = useSelector((state) => state.tasks.assignees);
	const assigneesStatus = useSelector((state) => state.tasks.assigneesStatus);
	const filters = useSelector((state) => state.tasks.filters);
	const error = useSelector((state) => state.tasks.error);
	const borderColor = useColorModeValue("gray.200", "gray.600");

	const [pageIndex, setPageIndex] = useState(0);
	const [pageCursors, setPageCursors] = useState([null]);
	const [statusFilter, setStatusFilter] = useState("");
	const [assigneeFilter, setAssigneeFilter] = useState("");
	const [sort, setSort] = useState("asc");
	const [selectedTaskId, setSelectedTaskId] = useState(null);
	const [editingTask, setEditingTask] = useState(null);
	const [taskToDelete, setTaskToDelete] = useState(null);
	const mutationStatus = useSelector((state) => state.tasks.mutationStatus);
	const assigneeOptions = useMemo(() => suggestionOptions(assignees), [assignees]);
	const selectedAssigneeOption = assigneeFilter ? { label: assigneeFilter, value: assigneeFilter } : null;

	const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
	const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
	const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

	const loadPage = useCallback(
		(index, cursor, nextFilters) => {
			const filterState = {
				...filters,
				...nextFilters,
			};
			delete filterState.cursor;
			const query = {
				...filterState,
				cursor: cursor || undefined,
			};
			dispatch(setTaskFilters(filterState));
			return dispatch(listAgencyTasksAsync(query)).then((result) => {
				if (result.error) {
					toast({
						title: t("tasks.loadError"),
						description: result.payload || result.error.message,
						status: "error",
						duration: 5000,
						isClosable: true,
					});
					return;
				}
				setPageCursors((prev) => {
					const updated = [...prev.slice(0, index + 1)];
					if (result.payload?.nextCursor) updated[index + 1] = result.payload.nextCursor;
					return updated;
				});
			});
		},
		[dispatch, filters, t, toast]
	);

	useEffect(() => {
		dispatch(listAgencyTaskAssigneesAsync());
		loadPage(0, null, { status: "", assignee: "", sort: "asc", limit: 10 });
		// initial load only
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (error) {
			toast({
				title: t("tasks.loadError"),
				description: error,
				status: "error",
				duration: 4000,
				isClosable: true,
			});
		}
	}, [error, t, toast]);

	const applyFilters = () => {
		setPageIndex(0);
		setPageCursors([null]);
		loadPage(0, null, {
			status: statusFilter,
			assignee: assigneeFilter.trim(),
			sort,
			limit: 10,
		});
	};

	const openCreate = () => {
		setEditingTask(null);
		onFormOpen();
	};

	const openEdit = (task) => {
		setEditingTask(task);
		onDetailClose();
		onFormOpen();
	};

	const openDetail = (taskId) => {
		setSelectedTaskId(taskId);
		onDetailOpen();
	};

	const openDeleteConfirmation = (task) => {
		setTaskToDelete(task);
		onDeleteOpen();
	};

	const closeDeleteConfirmation = () => {
		onDeleteClose();
		setTaskToDelete(null);
	};

	const handleDeleteTask = () => {
		if (!taskToDelete?.id) return;
		dispatch(deleteAgencyTaskAsync(taskToDelete.id)).then((result) => {
			if (result.error) {
				toast({
					title: t("tasks.deleteError"),
					description: result.payload || result.error.message,
					status: "error",
					duration: 5000,
					isClosable: true,
				});
				return;
			}
			toast({
				title: t("tasks.deleteSuccess"),
				status: "success",
				duration: 2000,
				isClosable: true,
			});
			if (selectedTaskId === taskToDelete.id) {
				onDetailClose();
				setSelectedTaskId(null);
			}
			closeDeleteConfirmation();
			handleSaved();
		});
	};

	const handleSaved = () => {
		dispatch(listAgencyTaskAssigneesAsync());
		loadPage(pageIndex, pageCursors[pageIndex] || null, {
			status: statusFilter,
			assignee: assigneeFilter.trim(),
			sort,
			limit: 10,
		});
	};

	const goPrev = () => {
		if (pageIndex === 0) return;
		const nextIndex = pageIndex - 1;
		setPageIndex(nextIndex);
		loadPage(nextIndex, pageCursors[nextIndex] || null, {
			status: statusFilter,
			assignee: assigneeFilter.trim(),
			sort,
			limit: 10,
		});
	};

	const goNext = () => {
		if (!nextCursor) return;
		const nextIndex = pageIndex + 1;
		setPageIndex(nextIndex);
		loadPage(nextIndex, nextCursor, {
			status: statusFilter,
			assignee: assigneeFilter.trim(),
			sort,
			limit: 10,
		});
	};

	return (
		<Flex direction="column">
			<Card overflowX={{ sm: "scroll", xl: "hidden" }}>
				<CardHeader>
					<HStack spacing={3} justify="space-between" align="start" flexWrap="wrap">
						<HStack spacing={3}>
							<Icon as={FiCheckSquare} w={6} h={6} color="blue.500" />
							<Text fontSize="2xl" color={textColor} fontWeight="bold">
								{t("tasks.tasks")}
							</Text>
						</HStack>
						<Button leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreate}>
							{t("tasks.addTask")}
						</Button>
					</HStack>
				</CardHeader>
				<CardBody>
					<VStack spacing={4} align="stretch">
						<SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
							<FormControl>
								<Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
									<option value="">{t("tasks.allStatuses")}</option>
									<option value="open">{t("tasks.statusValues.open")}</option>
									<option value="inProgress">{t("tasks.statusValues.inProgress")}</option>
									<option value="done">{t("tasks.statusValues.done")}</option>
									<option value="cancelled">{t("tasks.statusValues.cancelled")}</option>
								</Select>
							</FormControl>
							<FormControl>
								<AssigneeSelect
									isLoading={assigneesStatus === "loading"}
									borderColor={borderColor}
									options={assigneeOptions}
									value={selectedAssigneeOption}
									onChange={(option) => setAssigneeFilter(option?.value || "")}
									onCreateOption={(input) => setAssigneeFilter(input.trim())}
									placeholder={t("tasks.filterAssignee")}
									formatCreateLabel={(input) => t("tasks.filterAssigneePartial", { name: input })}
									noOptionsMessage={() => t("tasks.noAssignees")}
									onDeleted={(normalizedName) => {
										if (normalizeAssigneeKey(assigneeFilter) === normalizedName) {
											setAssigneeFilter("");
										}
									}}
								/>
							</FormControl>
							<FormControl>
								<Select value={sort} onChange={(e) => setSort(e.target.value)}>
									<option value="asc">{t("tasks.sortDueAsc")}</option>
									<option value="desc">{t("tasks.sortDueDesc")}</option>
								</Select>
							</FormControl>
							<Button colorScheme="blue" variant="outline" onClick={applyFilters} isLoading={listStatus === "loading"}>
								{t("tasks.applyFilters")}
							</Button>
						</SimpleGrid>

						<Table variant="simple" color={textColor} size="sm">
							<Thead>
								<Tr>
									<Th>{t("tasks.title")}</Th>
									<Th>{t("tasks.assignee")}</Th>
									<Th>{t("tasks.dueDate")}</Th>
									<Th>{t("tasks.status")}</Th>
									<Th></Th>
								</Tr>
							</Thead>
							<Tbody>
								{tasks.map((task) => {
									const urgency = dueDateUrgency(task);
									return (
										<Tr
											key={task.id}
											bg={urgency === "overdue" ? "red.50" : urgency === "soon" ? "yellow.50" : undefined}
											cursor="pointer"
											_hover={{ bg: urgency === "overdue" ? "red.100" : urgency === "soon" ? "yellow.100" : "gray.50" }}
											onClick={() => openDetail(task.id)}
										>
											<Td fontWeight="medium">{task.title}</Td>
											<Td>{task.assignee}</Td>
											<Td>
												<DueDateBadge task={task} />
											</Td>
											<Td>
												<Badge colorScheme={statusColor(task.status)}>{t(`tasks.statusValues.${task.status}`)}</Badge>
											</Td>
											<Td onClick={(e) => e.stopPropagation()}>
												<Menu>
													<MenuButton
														as={IconButton}
														aria-label={t("tasks.taskActions")}
														icon={<HamburgerIcon />}
														size="sm"
														variant="ghost"
														onClick={(e) => e.stopPropagation()}
													/>
													<MenuList>
														<MenuItem onClick={() => openDetail(task.id)}>{t("tasks.viewTask")}</MenuItem>
														<MenuItem onClick={() => openEdit(task)}>{t("misc.edit")}</MenuItem>
														<MenuItem color="red.500" onClick={() => openDeleteConfirmation(task)}>
															{t("table.delete")}
														</MenuItem>
													</MenuList>
												</Menu>
											</Td>
										</Tr>
									);
								})}
								{tasks.length === 0 && (
									<Tr>
										<Td colSpan={5}>
											<Text color="gray.500" textAlign="center">
												{listStatus === "loading" ? t("misc.loading") : t("table.noData")}
											</Text>
										</Td>
									</Tr>
								)}
							</Tbody>
						</Table>

						<HStack justify="space-between">
							<Badge variant="subtle" colorScheme="blue">
								{t("table.page")} {pageIndex + 1}
							</Badge>
							<ButtonGroup size="sm" variant="outline">
								<IconButton aria-label="Previous" icon={<FiChevronLeft />} onClick={goPrev} isDisabled={pageIndex === 0 || listStatus === "loading"} />
								<IconButton aria-label="Next" icon={<FiChevronRight />} onClick={goNext} isDisabled={!nextCursor || listStatus === "loading"} />
							</ButtonGroup>
						</HStack>
					</VStack>
				</CardBody>
			</Card>

			<TaskFormModal isOpen={isFormOpen} onClose={onFormClose} task={editingTask} onSaved={handleSaved} />
			<TaskDetailModal
				isOpen={isDetailOpen}
				onClose={onDetailClose}
				taskId={selectedTaskId}
				onEdit={openEdit}
			/>
			<PropertyConfirmModal
				isOpen={isDeleteOpen}
				onClose={closeDeleteConfirmation}
				onConfirm={handleDeleteTask}
				title={t("property.confirmDelete")}
				message={t("tasks.deleteTaskConfirmation")}
				emphasis={t("property.actionCannotBeUndone")}
				isLoading={mutationStatus === "loading"}
			/>
		</Flex>
	);
}
